import {useState} from "react";
import {App as AntdApp, Alert, Collapse, Table, Tag, Tooltip} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon, Loading} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useAddKeywordItemsMutation, useGetKeywordOpportunitiesQuery} from "~/reduxs/api/keywordApiSlice";
import ConnectGscCta from "./ConnectGscCta";
import style from "../style/Keyword.module.scss";

const cn = className.bind(style);

const pct = (v) => `${(v * 100).toFixed(1)}%`;

/**
 * Tab "Cơ hội": 3 khối tính từ dữ liệu GSC 90 ngày —
 *  1. Gần trang 1 (vị trí 5–20, đủ impressions): tối ưu on-page/link là bứt lên top.
 *  2. CTR thấp bất thường (đã trang 1 nhưng CTR < 1/2 kỳ vọng theo vị trí): sửa title/meta.
 *  3. Cụm chủ đề (nhiều truy vấn cùng gốc, chưa từ nào top 5): cơ hội làm trang chuyên sâu.
 */
function KeywordOpportunities({projectId}) {

	const {notification} = AntdApp.useApp();
	const {data, isLoading, isFetching} = useGetKeywordOpportunitiesQuery({projectId});
	const [addItems, {isLoading: saving}] = useAddKeywordItemsMutation();

	// Nút lưu vừa bấm (để chỉ nút đó quay loading thay vì cả 3 bảng).
	const [savingKey, setSavingKey] = useState(null);

	const save = async (rows, key) => {
		if (!rows.length) return;
		try {
			setSavingKey(key);
			const res = await addItems({
				projectId,
				items: rows.map((r) => ({keyword: r.keyword, source: 'gsc', impressions: r.impressions, position: r.position})),
			}).unwrap();
			if (res?.added > 0) {
				notification.success({message: 'Đã lưu vào kho', description: `${res.added} từ khoá mới${res.skipped ? ` (${res.skipped} đã có sẵn)` : ''}.`});
			} else {
				notification.info({message: 'Đã có', description: 'Các từ khoá này đã có trong kho.'});
			}
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không lưu được từ khoá')});
		} finally {
			setSavingKey(null);
		}
	};

	if (isLoading) return <Loading />;

	if (data && data.connected === false) {
		return <ConnectGscCta projectId={projectId} message="Tab Cơ hội tính từ dữ liệu tìm kiếm thật của Google Search Console — dự án này chưa kết nối." />;
	}

	const saveColumn = (keyPrefix) => ({
		title: '', key: 'action', align: 'right', width: 60,
		render: (_, r) => (
			<Tooltip title={r.saved ? 'Đã có trong kho' : 'Lưu vào kho từ khoá'}>
				<Button small white disabled={r.saved || saving} loading={savingKey === `${keyPrefix}-${r.keyword}`}
					onClick={() => save([r], `${keyPrefix}-${r.keyword}`)}
					leftIcon={<FontAwesomeIcon icon="fa-light fa-bookmark" />} />
			</Tooltip>
		),
	});

	const keywordCol = {
		title: 'Từ khoá', dataIndex: 'keyword', key: 'keyword', ellipsis: true,
		render: (v, r) => (
			<span>
				{v}{' '}
				{r.saved && <FontAwesomeIcon icon="fa-solid fa-circle-check" style={{color: '#16a34a'}} />}
			</span>
		),
	};

	const baseCols = [
		// Clicks/CTR là cột phụ — ẩn dưới 768px cho gọn mobile.
		{title: 'Clicks', dataIndex: 'clicks', key: 'clicks', width: 90, sorter: (a, b) => a.clicks - b.clicks, responsive: ['md'], render: (v) => v.toLocaleString('vi-VN')},
		{title: 'Impressions', dataIndex: 'impressions', key: 'impressions', width: 120, sorter: (a, b) => a.impressions - b.impressions, render: (v) => v.toLocaleString('vi-VN')},
		{title: 'CTR', dataIndex: 'ctr', key: 'ctr', width: 80, responsive: ['md'], render: (v) => pct(v)},
		{title: 'Vị trí', dataIndex: 'position', key: 'position', width: 80, sorter: (a, b) => a.position - b.position, render: (v) => Number(v).toFixed(1)},
	];

	const striking = data?.striking || [];
	const lowCtr = data?.low_ctr || [];
	const clusters = data?.clusters || [];
	const rangeText = data?.range ? `${data.range.from} → ${data.range.to}` : '';

	return (
		<div>
			<Alert type="info" showIcon style={{marginBottom: 14}}
				message={`Cơ hội tính từ dữ liệu Search Console ${rangeText ? `(${rangeText})` : ''}: ưu tiên việc mang lại thêm click nhanh nhất thay vì chạy theo từ khoá mới.`} />

			<div className={cn('section')}>
				<h3 className={cn('sectionTitle')}>
					<FontAwesomeIcon icon="fa-light fa-stairs" /> Gần trang 1 (vị trí 5–20) — {striking.length} từ khoá
				</h3>
				<p className={cn('hint')} style={{margin: '0 0 12px'}}>
					Đã có thứ hạng + nhiều lượt hiển thị; cải thiện nội dung/liên kết nội bộ cho các trang này thường bứt lên top nhanh nhất.
				</p>
				<Table rowKey="keyword" size="small" loading={isFetching}
					columns={[keywordCol, ...baseCols, saveColumn('striking')]}
					dataSource={striking}
					scroll={{x: 'max-content'}}
					pagination={{pageSize: 10, hideOnSinglePage: true, showSizeChanger: false}} />
			</div>

			<div className={cn('section')}>
				<h3 className={cn('sectionTitle')}>
					<FontAwesomeIcon icon="fa-light fa-arrow-pointer" /> CTR thấp bất thường — {lowCtr.length} từ khoá
				</h3>
				<p className={cn('hint')} style={{margin: '0 0 12px'}}>
					Đã ở trang 1 nhưng tỉ lệ click thấp hơn hẳn mức kỳ vọng theo vị trí → viết lại title/meta description hấp dẫn hơn.
				</p>
				<Table rowKey="keyword" size="small" loading={isFetching}
					columns={[
						keywordCol,
						...baseCols,
						{
							// Cột phụ — ẩn dưới 768px cho gọn mobile.
							title: 'CTR kỳ vọng', dataIndex: 'expected_ctr', key: 'expected', width: 110, responsive: ['md'],
							render: (v) => <span className={cn('hint')}>{pct(v)}</span>,
						},
						{
							title: 'Click bỏ lỡ', dataIndex: 'lost_clicks', key: 'lost', width: 100,
							sorter: (a, b) => a.lost_clicks - b.lost_clicks, defaultSortOrder: 'descend',
							render: (v) => <strong style={{color: '#dc2626'}}>≈{v.toLocaleString('vi-VN')}</strong>,
						},
						saveColumn('lowctr'),
					]}
					dataSource={lowCtr}
					scroll={{x: 'max-content'}}
					pagination={{pageSize: 10, hideOnSinglePage: true, showSizeChanger: false}} />
			</div>

			<div className={cn('section')}>
				<h3 className={cn('sectionTitle')}>
					<FontAwesomeIcon icon="fa-light fa-layer-group" /> Cụm chủ đề tiềm năng — {clusters.length} cụm
				</h3>
				<p className={cn('hint')} style={{margin: '0 0 12px'}}>
					Nhiều truy vấn cùng gốc có tổng hiển thị lớn nhưng chưa truy vấn nào lên top 5 — cơ hội xây trang chuyên sâu cho riêng chủ đề đó.
				</p>
				{clusters.length === 0 ? (
					<p className={cn('hint')}>Chưa phát hiện cụm chủ đề nào đủ lớn trong kỳ này.</p>
				) : (
					<Collapse
						items={clusters.map((c, i) => ({
							key: String(i),
							label: (
								<span>
									<strong>{c.label}</strong>
									<Tag style={{marginLeft: 10}}>{c.query_count} truy vấn</Tag>
									<Tag color="blue">{c.total_impressions.toLocaleString('vi-VN')} impressions</Tag>
									{c.best_position > 0 && <Tag color="orange">vị trí tốt nhất {Number(c.best_position).toFixed(1)}</Tag>}
								</span>
							),
							extra: (
								<Button small white disabled={saving} loading={savingKey === `cluster-${i}`}
									onClick={(e) => { e.stopPropagation(); save(c.queries.filter((r) => !r.saved), `cluster-${i}`); }}
									leftIcon={<FontAwesomeIcon icon="fa-light fa-bookmark" />}>
									Lưu cả cụm
								</Button>
							),
							children: (
								<Table rowKey="keyword" size="small"
									columns={[keywordCol, ...baseCols, saveColumn(`cluster-${i}`)]}
									dataSource={c.queries}
									scroll={{x: 'max-content'}}
									pagination={false} />
							),
						}))}
					/>
				)}
			</div>
		</div>
	);
}

export default KeywordOpportunities;
